import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcOrdersV1OrderQueryServiceOrderGet } from '@interxfi/api-client/generated/hooks';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN, LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container';
import { WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container/wrapping/wrapping-layout-container';
import { Typography } from '@interxfi/design-system/components/typography';
import { OrderEntityMenu } from '../../../../../../../../../../../features/order/order-entity-menu';
import { OrderEventSummary } from '../../../../../../../../../../../features/order/order-event';
import { OrderInstructionTaxLotSelectionDetail } from '../../../../../../../../../../../features/order/order-instruction-tax-lot-selection-detail';
import { getOrderDetailPageListItems } from '../../../../../../../../../../../features/order/order-list-items';
import { getOrderDetailPath } from '../../../../../../../../../../../features/order/order-routing';
import { OrderSidePill } from '../../../../../../../../../../../features/order/order-side-pill';
import { OrderStatusPill } from '../../../../../../../../../../../features/order/order-status-pill';
import { OrderSummaryBar } from '../../../../../../../../../../../features/order/order-summary-bar';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/order/equity/_equity/$orderId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-order-${params.orderId}`,
        label: 'Order',
        to: getOrderDetailPath(params.orderId, params.accountId),
        suffix: <CopyButton content={params.orderId} label="Copy Order ID" tooltip={{ copy: params.orderId, copied: 'Copied Order ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Order'),
  component: AccountOrderDetail,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load order', 'view this order');

function AccountOrderDetail() {
  const { accountId, orderId } = Route.useParams();
  const request = { orderId } as const;
  const { data, error, isPending } = useIxcOrdersV1OrderQueryServiceOrderGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getOrderDetailPageListItems(data?.order, { kind: 'account', accountId }), [data?.order, accountId]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          actions={<OrderEntityMenu orderId={orderId} />}
          pills={
            <>
              <OrderStatusPill isPending={showPending} size="large" status={data?.order?.status} />
              <OrderSidePill isPending={showPending} side={data?.order?.data?.instruction?.side} size="large" />
            </>
          }
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <OrderSummaryBar orderId={orderId} />
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <OrderEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: (
                <VerticalStack gap={4}>
                  <AppReadonlyListStack groups={listItems.left} isPending={showPending} />

                  {data?.order?.data?.instruction?.taxLotSelection ? (
                    <OrderInstructionTaxLotSelectionDetail
                      accountId={accountId}
                      assetId={data?.order?.data?.asset?.assetId}
                      taxLotSelection={data.order.data.instruction.taxLotSelection}
                    />
                  ) : null}
                </VerticalStack>
              ),
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
